import Papa from 'papaparse';
import domtoimage from 'dom-to-image';
import { win } from './dom';

// master_data.txt to graphs.json
// ------------------------------------------------

const pooledName = 'All countries pooled';

export const parseCSV = (csv) => {
	const parsed = Papa.parse(csv, {
		header: true,
		skipEmptyLines: 'greedy',
	});
	const { data } = parsed;
	const countries = { weeks: getWeeks(data, false, '2015-01'), countries: [] };
	const pooled = { weeks: getWeeks(data, true), groups: [] };

	const reportYear = data[0].yoai;
	const reportWeek = data[0].woai;

	for (let i = 0; i < data.length; i++) {
		const item = data[i];

		// If pooled
		if (item.country === pooledName) {
			const weekidx = pooled.weeks.indexOf(item.wk2);
			if (weekidx > -1) {
				const group = initGroup(pooled.groups, item.group);
				// zscore
				initArray(group, 'zscore', pooled.weeks.length);
				group.zscore[weekidx] = twoDecimals(parseFloat(item.zscore));
				// death count
				initArray(group, 'nbc', pooled.weeks.length);
				group.nbc[weekidx] = twoDecimals(parseFloat(item.nbc));
				// baseline
				initArray(group, 'pnb', pooled.weeks.length);
				group.pnb[weekidx] = twoDecimals(parseFloat(item.pnb));
				// excess
				initArray(group, 'excess', pooled.weeks.length);
				group.excess[weekidx] = twoDecimals(parseFloat(item.excess));
				// si = substantial increase (-4)
				initArray(group, 'si', pooled.weeks.length);
				group.si[weekidx] = twoDecimals(getRange(item, -4));
				// hnr = high normal range (-2)
				initArray(group, 'hnr', pooled.weeks.length);
				group.hnr[weekidx] = twoDecimals(getRange(item, -2));
				// lnr = low normal range (2)
				initArray(group, 'lnr', pooled.weeks.length);
				group.lnr[weekidx] = twoDecimals(getRange(item, 2));
			}
		}
		// If country
		else {
			const weekidx = countries.weeks.indexOf(item.wk2);
			if (weekidx > -1) {
				let country = find(countries.countries, 'country', item.country);
				if (!country) {
					country = { country: item.country, groups: [] };
					countries.countries.push(country);
				}
				const group = initGroup(country.groups, item.group);
				// zscore
				initArray(group, 'zscore', countries.weeks.length);
				group['zscore'][weekidx] = twoDecimals(parseFloat(item['zscore']));
			}
		}
	}

	const ages = pooled.groups.map((group) => group.group);

	return { reportYear, reportWeek, ages, pooled, countries };
};

// graphs.json to individual graph data
// -------------------------------------------------

export const getGraphsData = (graphsJson) => {
	return {
		reportYear: graphsJson.reportYear,
		reportWeek: graphsJson.reportWeek,
		countries: graphsJson.countries.countries.map((c) => c.country),
		ages: graphsJson.ages,
		pooledByAgeGroup: getPooledByAgeGroup(graphsJson),
		cumulatedExcessMortality: getCumulatedExcessMortality(graphsJson),
		zByCountry: getZByCountry(graphsJson),
		zByCountryMap: getZByCountryMap(graphsJson),
	};
};

// Used to turn a number into a redable string.
// Number can be a zscore or not
export const prettyNumber = (num, isZScore, ignoreZeroDecimals) => {
	if (!isZScore) {
		return Math.round(num).toLocaleString();
	} else {
	}
	const opts = { maximumFractionDigits: 2 };
	if (!ignoreZeroDecimals) {
		opts.minimumFractionDigits = 2;
	}
	return num.toLocaleString(undefined, opts);
};

// Takes a min and max number and returns an array of all
// tick values between those numbers, as defined by numTicks
export const calculateTickValues = (min, max, numTicks) => {
	const step = (max - min) / (numTicks - 1);
	const ticks = [];
	for (let i = 0; i < numTicks; i++) {
		ticks.push(min + i * step);
	}
	return ticks;
};

// Same as above, but for an array with .min/.max values
export const calculateGroupsTickValues = (minMaxVals, numTicks) => {
	const ticks = [];
	for (let i = 0; i < minMaxVals.length; i++) {
		ticks.push(calculateTickValues(minMaxVals[i].min, minMaxVals[i].max, numTicks));
	}
	return ticks;
};

export const calculateWeekTickLabels = (weeks) => {
	const tickLabels = [];
	for (let i = 0; i < weeks.length; i++) {
		const split = weeks[i].split('-');
		tickLabels.push(split[1] === '01' ? split[0] : split[1]);
	}
	return tickLabels;
};

// Expects input to be a groups array with
// And outputs and array with [[weekIdx, label]] that can be used in
// the format function of the x axis. Each array in the data array
// must have the same length and be equal to the length of the weeks array
export const calculateWeekTickValues = (data, weeks, numTicks) => {
	if (!data || !data[0] || !data[0].data || !data[0].data[0] || data[0].data[0].length === 0) {
		return null;
	}

	const firstData = data[0].data[0].data;

	const tickValues = [];
	const numYears = firstData.length / 52;
	const ticksPerYear = Math.floor(numTicks / numYears);
	const weekSpacing = Math.floor(52 / (ticksPerYear + 1));

	for (let i = 0; i < firstData.length; i++) {
		const split = weeks[firstData[i].x].split('-');
		const year = parseInt(split[0]);
		const week = parseInt(split[1]);
		if (week === 1 || (week % weekSpacing === 0 && week < 51)) {
			tickValues.push(firstData[i].x);
		}
	}

	return tickValues;
};

// Filters an array of objects based whether key is in the keyValues array
// Will also sort the returned array based on the keyvalues
export const filterByKey = (arr, key, keyValues) => {
	const newArr = [];
	for (let i = 0; i < arr.length; i++) {
		if (keyValues.includes(arr[i][key])) {
			newArr.push(arr[i]);
		}
	}
	return newArr.sort((a, b) => keyValues.indexOf(a[key]) - keyValues.indexOf(b[key]));
};

// Filters an array of groups to return only datasets with these ids
export const filterGroupsData = (groups, ids) => {
	const filtered = [];
	for (let i = 0; i < groups.length; i++) {
		const group = Object.assign({}, groups[i]);
		group.data = filterByKey(group.data, 'id', ids);
		filtered.push(group);
	}
	return filtered;
};

// Filters data[].x attributes to specific min and max index ranges
export const sliceGraphData = (data, min, max) => {
	const sliced = [];
	for (let i = 0; i < data.length; i++) {
		const item = Object.assign({}, data[i]);
		item.data = item.data.slice(min, max + 1);
		sliced.push(item);
	}
	return sliced;
};

// Same as above, but with an array of groups
export const sliceGroupsData = (groups, min, max) => {
	const sliced = [];
	for (let i = 0; i < groups.length; i++) {
		const group = Object.assign({}, groups[i]);
		group.data = sliceGraphData(group.data, min, max);
		sliced.push(group);
	}
	return sliced;
};

// Takes an array of objects and cumulates the key attribute
export const cumulate = (arr, key) => {
	const newArr = [];
	for (let i = 0; i < arr.length; i++) {
		const cum = i === 0 ? arr[i][key] : newArr[i - 1][key] + arr[i][key];
		newArr.push(Object.assign({}, arr[i], { [key]: cum }));
	}
	return newArr;
};

// Takes an array of groups and returns the lowest and highest
// value that exists in any group's data array.
// changeYs is a function that gets called for every value in minMaxVals
export const countGroupsMinMax = (groups, isZScore) => {
	const minMaxVals = [];
	let minY = Infinity;
	let maxY = -Infinity;
	let minX = Infinity;
	let maxX = -Infinity;
	for (let i = 0; i < groups.length; i++) {
		minMaxVals.push({ min: Infinity, max: -Infinity });
		for (let j = 0; j < groups[i].data.length; j++) {
			for (let k = 0; k < groups[i].data[j].data.length; k++) {
				const x = groups[i].data[j].data[k].x;
				const y = groups[i].data[j].data[k].y;
				if (y < minY) minY = y;
				if (y > maxY) maxY = y;
				if (x < minX) minX = x;
				if (x > maxX) maxX = x;
				if (y < minMaxVals[i].min) minMaxVals[i].min = y;
				if (y > minMaxVals[i].max) minMaxVals[i].max = y;
			}
		}
	}
	// Now change to proper numbers for the y axis min/max
	const padVal = isZScore ? 2 : Math.max(Math.abs(minY), Math.abs(maxY)) * 0.05;
	minY = yAxisMinMaxNumber(minY - padVal, true, isZScore);
	maxY = yAxisMinMaxNumber(maxY + padVal, false, isZScore);
	for (let i = 0; i < minMaxVals.length; i++) {
		const padVal = isZScore ? 2 : Math.max(Math.abs(minMaxVals[i].min), Math.abs(minMaxVals[i].max)) * 0.05;
		minMaxVals[i].min = yAxisMinMaxNumber(minMaxVals[i].min - padVal, true, isZScore);
		minMaxVals[i].max = yAxisMinMaxNumber(minMaxVals[i].max + padVal, false, isZScore);
	}

	return [minY, maxY, minMaxVals, minX, maxX];
};

// Is primarily used to round a number for the y-axis
// so we don't end up with values like 132,754 and -0.1345
// This function is only used to calculate min and max, not
// in between, since that would skew the y axis.
export const yAxisMinMaxNumber = (num, isMin, isZScore) => {
	if (isZScore) {
		return Math.round(num);
	} else {
		const rounded = Math.round(num);
		const numDigits = Math.abs(rounded).toString().length;
		if (numDigits > 2) {
			const roundTo = Math.pow(10, numDigits - 2);
			return isMin ? Math.floor(rounded / roundTo) * roundTo : Math.ceil(rounded / roundTo) * roundTo;
		} else {
			return rounded;
		}
	}
};

const initArray = (group, field, length) => {
	if (!group.hasOwnProperty(field)) {
		group[field] = new Array(length);
		// Set all to null
		for (let i = 0; i < group[field].length; i++) {
			group[field][i] = null;
		}
	}
};

const initGroup = (groups, newGroup) => {
	let group = find(groups, 'group', newGroup);
	if (!group) {
		group = { group: newGroup };
		groups.push(group);
	}
	return group;
};

// This calculates the normal ranges and substantial increase for
// a datapoint. Give it the item and the 2,4,8 factor.
const f1 = 2 / 3;
const f2 = 3 / 2;
const getRange = (item, factor) => {
	return Math.pow(
		Math.max(
			0,
			Math.pow(item.pnb, f1) - (factor * (Math.pow(item.nbc, f1) - Math.pow(item.pnb, f1))) / item.zscore
		),
		f2
	);
};

// Find weeks
// ------------------------------------------------
// A script to find all weeks in the dataset and sort ascending
// based on the year and month.

const getWeeks = (data, countTotal, cutoffWeek) => {
	const weeks = {};
	for (let i = 0; i < data.length; i++) {
		if ((!countTotal || data[i].country === pooledName) && !weeks.hasOwnProperty(data[i].wk2)) {
			weeks[data[i].wk2] = {
				wodi: data[i].wodi,
				yodi: data[i].yodi,
			};
		}
	}
	let keys = Object.keys(weeks);
	keys.sort((a, b) => {
		const weeka = weeks[a];
		const weekb = weeks[b];
		const yearDiff = weeka.yodi - weekb.yodi;
		if (yearDiff !== 0) {
			return yearDiff;
		} else {
			return weeka.wodi - weekb.wodi;
		}
	});

	// Always cut to max 6 years
	const maxWeeks = 52 * 6;
	if (keys.length > maxWeeks) {
		keys = keys.slice(-1 * maxWeeks);
	}

	// If cutoffweek is set, cut off at that week if still in array
	if (cutoffWeek) {
		const weekidx = keys.indexOf(cutoffWeek);
		if (weekidx > -1) {
			keys = keys.slice(weekidx);
		}
	}

	return keys;
};

// Find
// ------------------------------------------------
// A faster implementation of array.find using loop

const find = (arr, key, val) => {
	for (let i = 0; i < arr.length; i++) {
		if (arr[i][key] === val) {
			return arr[i];
		}
	}
	return null;
};

const twoDecimals = (zscore) => {
	return Math.floor(zscore * 100) / 100;
};

// Graph: PooledByAgeGroup
// ------------------------------------------------

const getPooledByAgeGroup = ({ pooled }) => {
	const counts = [];
	const zscores = [];
	for (let i = 0; i < pooled.groups.length; i++) {
		const group = pooled.groups[i];
		counts.push({
			id: group.group,
			data: [
				{ id: 'Baseline', data: arraysToXYArray(group.pnb) },
				{ id: 'Substantial increase', data: arraysToXYArray(group.si) },
				{ id: 'Observed count', data: arraysToXYArray(group.nbc) },
			],
			hnr: group.hnr,
			lnr: group.lnr,
		});
		zscores.push({
			id: group.group,
			data: [{ id: 'zscore', data: arraysToXYArray(group.zscore) }],
		});
	}
	return { counts, zscores, weeks: pooled.weeks };
};

export const getPooledByAgeGroupCSV = (data, weeks) => {
	const jsonArray = [];
	for (let i = 0; i < data.length; i++) {
		const ageGroup = data[i];
		// figure out how many items we have per data point based on first data array
		const numItems = ageGroup.data[0].data.length;
		for (let j = 0; j < numItems; j++) {
			const x = ageGroup.data[0].data[j].x;
			const item = {
				group: ageGroup.id,
				week: weeks[x],
			};
			if (ageGroup.lnr) {
				item['Low normal range'] = ageGroup.lnr[x];
			}
			if (ageGroup.hnr) {
				item['High normal range'] = ageGroup.hnr[x];
			}
			for (let k = 0; k < ageGroup.data.length; k++) {
				item[ageGroup.data[k].id] = ageGroup.data[k].data[j].y;
			}
			jsonArray.push(item);
		}
	}
	return Papa.unparse(jsonArray, { delimiter: ';' });
};

// Graph: CumulatedExcessMortality
// ------------------------------------------------

const getCumulatedExcessMortality = ({ pooled }) => {
	const data = [];
	for (let i = 0; i < pooled.groups.length; i++) {
		const group = pooled.groups[i];
		const gdata = { id: group.group, data: [] };
		for (let j = 0; j < group.excess.length; j++) {
			const split = pooled.weeks[j].split('-');
			const year = parseInt(split[0]);
			const week = parseInt(split[1]);
			if (!gdata.data[gdata.data.length - 1] || gdata.data[gdata.data.length - 1].id !== year) {
				// This dataset has a week 0, which is needed to cumulate from 0
				gdata.data.push({ id: year, data: [{ x: 0, y: 0 }] });
			}
			const point = { x: week, y: group.excess[j] };
			gdata.data[gdata.data.length - 1].data.push(point);
		}
		data.push(gdata);
	}

	// Find years based on first data source. They should all be the same
	const years = [];
	for (let i = 0; i < data[0].data.length; i++) {
		const value = data[0].data[i].id;
		years.push(value);
	}

	return { data, years };
};

export const getCumulatedExcessMortalityCSV = (data) => {
	const jsonArray = [];
	for (let i = 0; i < data.length; i++) {
		const group = data[i];
		for (let j = 0; j < group.data.length; j++) {
			const year = group.data[j];
			for (let k = 0; k < year.data.length; k++) {
				jsonArray.push({
					group: group.id,
					year: year.id,
					week: year.data[k].x,
					value: Math.round(year.data[k].y),
				});
			}
		}
	}
	return Papa.unparse(jsonArray, { delimiter: ';' });
};

// Graph: ZByCountry
// ------------------------------------------------

const getZByCountry = ({ countries }) => {
	const list = countries.countries;
	const data = [];
	for (let i = 0; i < list.length; i++) {
		const country = { id: list[i].country, data: [] };
		for (let j = 0; j < list[i].groups.length; j++) {
			const group = list[i].groups[j];
			country.data.push({
				id: group.group,
				data: [{ id: 'zscore', data: arraysToXYArray(group.zscore) }],
			});
		}
		data.push(country);
	}
	return { data, weeks: countries.weeks };
};

export const getZByCountryCSV = (data, weeks, selectedAge) => {
	const jsonArray = [];
	for (let i = 0; i < data.length; i++) {
		for (let j = 0; j < data[i].data[0].data.length; j++) {
			const dataPoint = data[i].data[0].data[j];
			jsonArray.push({
				country: data[i].id,
				group: selectedAge.value,
				week: weeks[dataPoint.x],
				zscore: dataPoint.y,
			});
		}
	}
	return Papa.unparse(jsonArray, { delimiter: ';' });
};

// Graph: ZByCountryMap
// ------------------------------------------------

const getZByCountryMap = ({ countries }) => {
	const list = countries.countries;
	const totals = list.map((country) => country.groups.find((group) => group.group === 'Total'));
	const data = [];
	for (let i = 0; i < countries.weeks.length; i++) {
		const weekData = [];
		for (let j = 0; j < list.length; j++) {
			weekData.push({
				id: list[j].country,
				value: totals[j].zscore[i],
			});
		}
		data.push(weekData);
	}

	return { data, weeks: countries.weeks };
};

export const getZByCountryMapCSV = (week, data) => {
	const weekData = data.data[week];
	const weekLabel = data.weeks[week];
	const jsonArray = [];
	for (let i = 0; i < weekData.length; i++) {
		jsonArray.push({
			week: weekLabel,
			country: weekData[i].id,
			zscore: weekData[i].value,
		});
	}
	return Papa.unparse(jsonArray, { delimiter: ';' });
};

const arraysToXYArray = (ys) => {
	const data = [];
	for (let i = 0; i < ys.length; i++) {
		data.push({
			x: i,
			y: ys[i],
		});
	}
	return data;
};

// Graphs: Generate images
// ------------------------------------------------

const toStringFnc = {}.toString;
export const canGenerateImage =
	win &&
	!!win.document.createElementNS &&
	/SVGForeignObject/.test(toStringFnc.call(document.createElementNS('http://www.w3.org/2000/svg', 'foreignObject')));

export const generateImage = async (ref, width, fileName, setSaving) => {
	setSaving(true);
	const padding = 40;
	const node = ref.current;
	const scale = width / node.offsetWidth;
	try {
		const dataUrl = await domtoimage.toPng(node, {
			quality: 1,
			bgcolor: 'white',
			height: (node.offsetHeight + 2 * padding) * scale,
			width: (node.offsetWidth + 2 * padding) * scale,
			style: {
				transform: 'scale(' + scale + ')',
				transformOrigin: 'top left',
				width: node.offsetWidth + 'px',
				height: node.offsetHeight + 'px',
				padding: padding + 'px',
			},
		});
		downloadFile(dataUrl, `${fileName}.png`);
		setSaving(false);
	} catch (e) {
		console.error('Something went wrong when generating the image!', error);
		setSaving(false);
	}
};

const downloadFile = (dataUrl, fileName) => {
	if (typeof document !== 'undefined') {
		const link = document.createElement('a');
		link.download = fileName;
		link.href = dataUrl;
		link.click();
	}
};
